import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { TreePine, Heart, Plus, ArrowRight, User, Settings, LogOut, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HomePage({ user }) {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [memorials, setMemorials] = useState([]);
  const [trees, setTrees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [memorialsRes, treesRes] = await Promise.all([
        axios.get(`${API}/memorials/my`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/family-trees`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setMemorials(memorialsRes.data || []);
      setTrees(treesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3">
            <TreePine className="w-8 h-8 text-primary" strokeWidth={1.5} />
            <h1 className="text-2xl font-serif font-semibold text-foreground">Sarmoh</h1>
          </Link>
          
          {/* Center Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <Button 
              variant="ghost"
              onClick={() => navigate('/create-memorial')}
              className="text-foreground-muted hover:text-primary hover:bg-primary/5 rounded-full px-5 py-2 font-sans text-sm"
            >
              <Heart className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Memorial Tribute
            </Button>
            <Button 
              variant="ghost"
              onClick={() => navigate('/tree-builder')}
              className="text-foreground-muted hover:text-primary hover:bg-primary/5 rounded-full px-5 py-2 font-sans text-sm"
            >
              <TreePine className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Family Tree
            </Button>
          </div>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-2 px-3 py-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-4 h-4 text-primary" />
                </div>
              )}
              <span className="text-sm font-sans text-foreground hidden sm:block">{user?.name}</span>
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </button>
            
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white border border-border rounded-lg shadow-lg py-1 z-50">
                <button
                  onClick={() => { setShowUserMenu(false); navigate('/settings'); }}
                  className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-gray-50 flex items-center"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Account Settings
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 flex items-center"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Welcome Section */}
      <section className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl font-serif text-foreground mb-2">
              Welcome back, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-lg font-sans text-foreground-muted">
              What would you like to do today?
            </p>
          </motion.div>
        </div>
      </section>

      {/* Action Cards */}
      <section className="pb-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Create Memorial Card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              onClick={() => navigate('/create-memorial')}
              className="group bg-white border border-border shadow-card rounded-2xl p-8 cursor-pointer hover:shadow-lg hover:border-primary/30 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-14 h-14 rounded-2xl bg-rose-100 flex items-center justify-center group-hover:bg-rose-200 transition-colors">
                  <Heart className="w-7 h-7 text-rose-600" strokeWidth={1.5} />
                </div>
                <Plus className="w-6 h-6 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
              <h2 className="text-2xl font-serif text-foreground mb-2">Add Tribute for a Loved One</h2>
              <p className="text-foreground-muted font-sans mb-4">
                Create a memorial page to collect messages, photos, and voice tributes from family and friends.
              </p>
              <span className="text-primary font-sans text-sm flex items-center group-hover:underline">
                Create Memorial <ArrowRight className="w-4 h-4 ml-1" />
              </span>
            </motion.div>

            {/* Build Family Tree Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              onClick={() => navigate('/tree-builder')}
              className="group bg-white border border-border shadow-card rounded-2xl p-8 cursor-pointer hover:shadow-lg hover:border-primary/30 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                  <TreePine className="w-7 h-7 text-emerald-600" strokeWidth={1.5} />
                </div>
                <Plus className="w-6 h-6 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
              <h2 className="text-2xl font-serif text-foreground mb-2">Build Your Family Tree</h2>
              <p className="text-foreground-muted font-sans mb-4">
                Create an interactive family tree with photos, relationships, and stories that span generations.
              </p>
              <span className="text-primary font-sans text-sm flex items-center group-hover:underline">
                Start Building <ArrowRight className="w-4 h-4 ml-1" />
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Recent Activity */}
      {(memorials.length > 0 || trees.length > 0) && (
        <section className="py-12 px-6 bg-paper">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-serif text-foreground mb-6">Your Recent Activity</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Recent Memorials */}
              {memorials.length > 0 && (
                <div>
                  <h3 className="text-lg font-sans text-foreground-muted mb-4 flex items-center">
                    <Heart className="w-5 h-5 mr-2 text-rose-500" />
                    Memorial Tributes
                  </h3>
                  <div className="space-y-3">
                    {memorials.slice(0, 3).map(memorial => (
                      <div
                        key={memorial.memorial_id}
                        onClick={() => navigate(`/tribute/${memorial.memorial_id}`)}
                        className="bg-white border border-border rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
                      >
                        <p className="font-serif text-foreground">{memorial.deceased_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {memorial.tribute_count || 0} tributes
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Trees */}
              {trees.length > 0 && (
                <div>
                  <h3 className="text-lg font-sans text-foreground-muted mb-4 flex items-center">
                    <TreePine className="w-5 h-5 mr-2 text-emerald-500" />
                    Family Trees
                  </h3>
                  <div className="space-y-3">
                    {trees.slice(0, 3).map(tree => (
                      <div
                        key={tree.tree_id}
                        onClick={() => navigate(`/tree-builder/${tree.tree_id}`)}
                        className="bg-white border border-border rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
                      >
                        <p className="font-serif text-foreground">{tree.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {tree.node_count || 0} members
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-8 px-6 bg-paper border-t border-border mt-auto">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <TreePine className="w-5 h-5 text-primary" strokeWidth={1.5} />
              <span className="text-lg font-serif text-foreground">Sarmoh</span>
            </div>
            <div className="flex items-center space-x-6">
              <Link to="/terms" className="text-sm text-muted-foreground hover:text-primary">Terms</Link>
              <Link to="/privacy" className="text-sm text-muted-foreground hover:text-primary">Privacy</Link>
            </div>
          </div>
          <div className="text-center mt-6 pt-4 border-t border-border">
            <p className="text-sm text-muted-foreground">© 2026 TLML Mediatech Pvt Ltd. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
